import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, X, Send, Bot, ArrowRight, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { aiAPI } from '../lib/api';

const GREETING_DELAY = 5000;
const SESSION_KEY = 'xceed-chat-session-id';

const INITIAL_MESSAGE = {
  role: 'assistant',
  text: "Hi! I'm the XceedAgents AI assistant. Tell me what you'd like built or automated — a WhatsApp bot, a dashboard, an ERP/CRM agent, a full product — and I'll help you scope it and prepare a free proposal.",
};

function makeSessionId() {
  const existing = sessionStorage.getItem(SESSION_KEY);
  if (existing) return existing;
  const id = `chat-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  sessionStorage.setItem(SESSION_KEY, id);
  return id;
}

export default function FloatingAIChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [showPulse, setShowPulse] = useState(false);
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [collectedFields, setCollectedFields] = useState({});
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittedLeadId, setSubmittedLeadId] = useState(null);
  const messagesEndRef = useRef(null);
  const sessionIdRef = useRef(makeSessionId());
  const navigate = useNavigate();

  // Auto-pop greeting after 5 seconds
  useEffect(() => {
    const sessionGreeted = sessionStorage.getItem('xceed-chat-greeted');
    if (sessionGreeted) return;

    const timer = setTimeout(() => {
      setShowPulse(true);
      sessionStorage.setItem('xceed-chat-greeted', 'true');
    }, GREETING_DELAY);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const handleSend = async () => {
    if (!input.trim() || isTyping) return;

    const userMsg = input.trim();
    setInput('');
    const updatedHistory = [...messages, { role: 'user', text: userMsg }];
    setMessages(updatedHistory);
    setIsTyping(true);

    try {
      const res = await aiAPI.chat(
        sessionIdRef.current,
        userMsg,
        updatedHistory.slice(-10).map(m => ({ role: m.role, text: m.text }))
      );
      const { reply, fields = {}, ready = false } = res.data || {};

      // Merge collected fields
      const merged = { ...collectedFields, ...fields };
      setCollectedFields(merged);
      setIsReady(ready);

      const assistantMsg = {
        role: 'assistant',
        text: reply || "Got it! Could you tell me a bit more about the project?",
        action: ready ? 'generate-proposal' : null,
      };
      setMessages(prev => [...prev, assistantMsg]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: "I'm having trouble connecting right now. You can also fill out our proposal form directly.",
        action: 'start-project',
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleGenerateProposal = async () => {
    if (submitting) return;
    setSubmitting(true);
    setIsTyping(true);
    try {
      const payload = {
        name: collectedFields.name || 'Prospective Client',
        email: collectedFields.email,
        country: collectedFields.country || 'Unknown',
        description: collectedFields.description || messages.filter(m => m.role === 'user').map(m => m.text).join(' '),
        budget_range: collectedFields.budget_range || null,
        timeline: collectedFields.timeline || null,
        project_type: collectedFields.project_type || null,
        source: 'website',
      };
      const res = await aiAPI.evaluate(payload);
      const leadId = res.data?.lead_id;
      setSubmittedLeadId(leadId);
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: "Your AI proposal is ready! You can preview the full breakdown or download a PDF.",
        action: 'view-proposal',
        leadId,
      }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: "I couldn't generate the proposal right now. Please try the full form for best results.",
        action: 'start-project',
      }]);
    } finally {
      setSubmitting(false);
      setIsTyping(false);
    }
  };

  const handleDownloadPDF = async (leadId) => {
    try {
      const res = await aiAPI.downloadProposalPDF(leadId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `XceedAgents-Proposal.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // Silent fail; the proposal still lives in admin
    }
  };

  return (
    <>
      {/* Chat Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            className="fixed bottom-6 right-6 z-50"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20 }}
          >
            {showPulse && (
              <div className="absolute inset-0 rounded-full bg-blue-500/30" style={{ animation: 'pulse-ring 2s ease-out infinite' }} />
            )}
            <button
              onClick={() => { setIsOpen(true); setShowPulse(false); }}
              className="relative w-14 h-14 rounded-full flex items-center justify-center shadow-float transition-transform hover:scale-105"
              style={{ background: 'var(--primary)', color: 'var(--background)' }}
              data-testid="ai-chat-trigger"
              aria-label="Open AI chat"
            >
              <MessageSquare className="w-6 h-6" />
            </button>
            {showPulse && (
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2" style={{ borderColor: 'var(--background)' }} />
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed bottom-6 right-6 z-50 w-[380px] max-h-[560px] rounded-2xl overflow-hidden shadow-float flex flex-col"
            style={{ background: 'var(--background)', border: '1px solid var(--border)' }}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            data-testid="ai-chat-window"
          >
            {/* Header */}
            <div
              className="px-5 py-4 flex items-center justify-between border-b"
              style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--tag-bg)' }}>
                  <Bot className="w-4 h-4" style={{ color: 'var(--primary-accent)' }} />
                </div>
                <div>
                  <div className="text-sm font-semibold">XceedAgents AI</div>
                  <div className="text-xs flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Online
                  </div>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 rounded-lg transition-colors hover:bg-zinc-100 dark:hover:bg-zinc-800"
                data-testid="ai-chat-close"
                aria-label="Close chat"
              >
                <X className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 chat-messages" style={{ maxHeight: '360px', minHeight: '280px' }}>
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                      msg.role === 'user' ? 'rounded-br-md' : 'rounded-bl-md'
                    }`}
                    style={msg.role === 'user'
                      ? { background: 'var(--primary)', color: 'var(--background)' }
                      : { background: 'var(--surface)', color: 'var(--text-primary)' }
                    }
                    data-testid={`chat-msg-${i}`}
                  >
                    {msg.text}

                    {msg.action === 'generate-proposal' && !submittedLeadId && (
                      <button
                        onClick={handleGenerateProposal}
                        disabled={submitting}
                        className="mt-3 flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60"
                        style={{ background: 'var(--primary-accent)', color: '#fff' }}
                        data-testid="chat-generate-proposal"
                      >
                        <Sparkles className="w-3 h-3" />
                        {submitting ? 'Generating...' : 'Generate My Proposal'}
                      </button>
                    )}

                    {msg.action === 'view-proposal' && msg.leadId && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          onClick={() => { setIsOpen(false); navigate(`/proposal/${msg.leadId}`); }}
                          className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
                          style={{ background: 'var(--primary-accent)', color: '#fff' }}
                          data-testid="chat-view-proposal"
                        >
                          View Full Proposal <ArrowRight className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => handleDownloadPDF(msg.leadId)}
                          className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors border"
                          style={{ borderColor: 'var(--border)', color: 'var(--text-primary)' }}
                          data-testid="chat-download-pdf"
                        >
                          Download PDF
                        </button>
                      </div>
                    )}

                    {msg.action === 'start-project' && (
                      <button
                        onClick={() => { setIsOpen(false); navigate('/start-project'); }}
                        className="mt-3 flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
                        style={{ background: 'var(--primary-accent)', color: '#fff' }}
                        data-testid="chat-start-project"
                      >
                        Open Proposal Form <ArrowRight className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {isTyping && (
                <div className="flex justify-start">
                  <div className="px-4 py-3 rounded-2xl rounded-bl-md" style={{ background: 'var(--surface)' }}>
                    <div className="flex gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-zinc-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 rounded-full bg-zinc-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 rounded-full bg-zinc-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t" style={{ borderColor: 'var(--border)' }}>
              <div
                className="flex items-center gap-2 rounded-xl px-4 py-2.5"
                style={{ background: 'var(--input-bg)', border: '1px solid var(--input-border)' }}
              >
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Describe what you need built..."
                  className="flex-1 bg-transparent text-sm outline-none"
                  style={{ color: 'var(--text-primary)' }}
                  data-testid="ai-chat-input"
                  disabled={isTyping || submitting}
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || isTyping || submitting}
                  className="p-1.5 rounded-lg transition-colors disabled:opacity-30"
                  style={{ color: 'var(--primary-accent)' }}
                  data-testid="ai-chat-send"
                  aria-label="Send message"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <p className="text-[10px] text-center mt-2" style={{ color: 'var(--text-muted)' }}>
                Powered by XceedAgents AI · Free proposal in 60 seconds
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
